import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Target, Plus, Edit, Trash2, ArrowLeft, Search, MoreHorizontal, Calendar,
  DollarSign, Users, ShoppingCart, TrendingUp, TrendingDown, Loader2, Flag,
  CheckCircle2, AlertCircle, Clock, Pause
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import api from "@/lib/api";
import { format, parseISO, differenceInDays } from "date-fns";

export default function ObjectivesPage() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [objectives, setObjectives] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [showDialog, setShowDialog] = useState(false);
  const [editingObjective, setEditingObjective] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showProgressDialog, setShowProgressDialog] = useState(null);
  const [progressValue, setProgressValue] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    objective_type: "revenue_target",
    target_value: "",
    target_unit: "currency",
    currency: "USD",
    period_type: "yearly",
    start_date: new Date().toISOString().split('T')[0],
    end_date: "",
    priority: 0
  });

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get("/financial-objectives/summary");
      setObjectives(response.data.objectives || []);
      setSummary({
        total: response.data.total_objectives,
        active: response.data.active_objectives,
        completed: response.data.completed_objectives,
        on_track: response.data.on_track_count,
        at_risk: response.data.at_risk_count
      });
    } catch (error) {
      console.error("Error fetching objectives:", error);
      if (error.response?.status === 403) {
        toast.error("This feature is only available for business accounts");
        navigate("/dashboard");
      } else {
        toast.error(t("failed_load_data", "dashboard") || "Failed to load data");
      }
    } finally {
      setIsLoading(false);
    }
  }, [navigate, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (searchParams.get("action") === "new") {
      openCreateDialog();
    }
  }, [searchParams]);

  const openCreateDialog = () => {
    setEditingObjective(null);
    const now = new Date();
    const yearEnd = new Date(now.getFullYear(), 11, 31);
    setFormData({
      name: "",
      description: "",
      objective_type: "revenue_target",
      target_value: "",
      target_unit: "currency",
      currency: "USD",
      period_type: "yearly",
      start_date: now.toISOString().split('T')[0],
      end_date: yearEnd.toISOString().split('T')[0],
      priority: 0
    });
    setShowDialog(true);
  };

  const openEditDialog = (objective) => {
    setEditingObjective(objective);
    setFormData({
      name: objective.name,
      description: objective.description || "",
      objective_type: objective.objective_type,
      target_value: objective.target_value.toString(),
      target_unit: objective.target_unit,
      currency: objective.currency,
      period_type: objective.period_type,
      start_date: objective.start_date?.split('T')[0] || "",
      end_date: objective.end_date?.split('T')[0] || "",
      priority: objective.priority
    });
    setShowDialog(true);
  };

  const handleObjectiveTypeChange = (type) => {
    let unit = "currency";
    switch (type) {
      case "client_acquisition": unit = "clients"; break;
      case "sales_volume": unit = "units"; break;
      case "profit_margin": 
      case "expense_reduction": unit = "percentage"; break;
      default: unit = "currency";
    }
    setFormData({...formData, objective_type: type, target_unit: unit});
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error("Please enter an objective name");
      return;
    }
    if (!formData.target_value || parseFloat(formData.target_value) <= 0) {
      toast.error("Please enter a valid target value");
      return;
    }
    if (!formData.end_date) {
      toast.error("Please select an end date");
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        ...formData,
        target_value: parseFloat(formData.target_value),
        priority: parseInt(formData.priority) || 0,
        start_date: new Date(formData.start_date).toISOString(),
        end_date: new Date(formData.end_date).toISOString()
      };

      if (editingObjective) {
        await api.put(`/financial-objectives/${editingObjective.id}`, payload);
        toast.success(t("objective_updated", "company") || "Objective updated successfully");
      } else {
        await api.post("/financial-objectives", payload);
        toast.success(t("objective_created", "company") || "Objective created successfully");
      }
      
      setShowDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    
    try {
      await api.delete(`/financial-objectives/${deleteConfirm.id}`);
      toast.success(t("objective_deleted", "company") || "Objective deleted successfully");
      setDeleteConfirm(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete");
    }
  };

  const handleUpdateProgress = async () => {
    if (!showProgressDialog || !progressValue) return;
    
    try {
      await api.patch(`/financial-objectives/${showProgressDialog.id}/progress?current_value=${parseFloat(progressValue)}`);
      toast.success(t("progress_updated", "company") || "Progress updated successfully");
      setShowProgressDialog(null);
      setProgressValue("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update progress");
    }
  };

  const handleStatusChange = async (objective, newStatus) => {
    try {
      await api.put(`/financial-objectives/${objective.id}`, { status: newStatus });
      toast.success(t("status_updated", "company") || "Status updated");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    }
  };

  const formatCurrency = (amount, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatValue = (value, unit, currency) => {
    switch (unit) {
      case "currency": return formatCurrency(value, currency);
      case "percentage": return `${value}%`;
      case "clients": return `${value} clients`;
      case "units": return `${value} units`;
      default: return value.toString();
    }
  };

  const getObjectiveIcon = (type) => {
    switch (type) {
      case "revenue_target": return DollarSign;
      case "client_acquisition": return Users;
      case "sales_volume": return ShoppingCart;
      case "profit_margin": return TrendingUp;
      case "expense_reduction": return TrendingDown;
      default: return Target;
    }
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case "active": return { label: t("active", "common") || "Active", variant: "default", icon: Flag };
      case "completed": return { label: t("completed", "common") || "Completed", variant: "outline", icon: CheckCircle2, className: "text-green-600 border-green-600" };
      case "paused": return { label: t("paused", "company") || "Paused", variant: "secondary", icon: Pause };
      case "failed": return { label: t("failed", "company") || "Failed", variant: "destructive", icon: AlertCircle };
      default: return { label: status, variant: "outline", icon: Clock };
    }
  };

  const getProgressColor = (progress, daysRemaining, totalDays) => {
    if (progress >= 100) return "bg-green-500";
    const expectedProgress = ((totalDays - daysRemaining) / totalDays) * 100;
    if (progress >= expectedProgress * 0.8) return "bg-green-500";
    if (progress >= expectedProgress * 0.5) return "bg-yellow-500";
    return "bg-orange-500";
  };

  const filteredObjectives = objectives.filter(obj => {
    return filterStatus === "all" || obj.status === filterStatus;
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b bg-card">
          <div className="max-w-[1200px] mx-auto px-4 py-4">
            <Skeleton className="h-8 w-48" />
          </div>
        </header>
        <main className="max-w-[1200px] mx-auto px-4 py-6">
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-32 w-full" />
            ))}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="objectives-page">
      {/* Header */}
      <header className="border-b bg-card sticky top-0 z-10">
        <div className="max-w-[1200px] mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} data-testid="back-button">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="font-semibold text-lg flex items-center gap-2">
                <Target className="h-5 w-5 text-[hsl(var(--ff-primary))]" />
                {t("financial_objectives", "company") || "Financial Objectives"}
              </h1>
              <p className="text-xs text-muted-foreground">{objectives.length} {t("total", "common") || "total"}</p>
            </div>
          </div>
          
          <Button onClick={openCreateDialog} data-testid="new-objective-button">
            <Plus className="h-4 w-4 mr-2" />
            {t("new_objective", "company") || "New Objective"}
          </Button>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-4 py-6 space-y-4">
        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold">{summary.total}</p>
                <p className="text-xs text-muted-foreground">{t("total", "common") || "Total"}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-blue-600">{summary.active}</p>
                <p className="text-xs text-muted-foreground">{t("active", "common") || "Active"}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-green-600">{summary.completed}</p>
                <p className="text-xs text-muted-foreground">{t("completed", "common") || "Completed"}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-green-600">{summary.on_track}</p>
                <p className="text-xs text-muted-foreground">{t("on_track", "company") || "On Track"}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-orange-600">{summary.at_risk}</p>
                <p className="text-xs text-muted-foreground">{t("at_risk", "company") || "At Risk"}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filter */}
        <div className="flex gap-3">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[180px]" data-testid="status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all_statuses", "company") || "All Statuses"}</SelectItem>
              <SelectItem value="active">{t("active", "common") || "Active"}</SelectItem>
              <SelectItem value="completed">{t("completed", "common") || "Completed"}</SelectItem>
              <SelectItem value="paused">{t("paused", "company") || "Paused"}</SelectItem>
              <SelectItem value="failed">{t("failed", "company") || "Failed"}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Objectives List */}
        {filteredObjectives.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Target className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">
                {filterStatus !== "all" ? (t("no_objectives_status", "company") || "No objectives with this status") : (t("no_objectives_yet", "company") || "No financial objectives yet")}
              </p>
              {filterStatus === "all" && (
                <Button className="mt-4" onClick={openCreateDialog}>
                  <Plus className="h-4 w-4 mr-2" />
                  {t("create_first_objective", "company") || "Create Your First Objective"}
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredObjectives.map((obj) => {
              const Icon = getObjectiveIcon(obj.objective_type);
              const statusConfig = getStatusConfig(obj.status);
              const StatusIcon = statusConfig.icon;
              const startDate = obj.start_date ? parseISO(obj.start_date) : new Date();
              const endDate = obj.end_date ? parseISO(obj.end_date) : new Date();
              const totalDays = differenceInDays(endDate, startDate);
              
              return (
                <Card key={obj.id} data-testid={`objective-card-${obj.id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-lg bg-[hsl(var(--ff-primary)/.1)]">
                          <Icon className="h-5 w-5 text-[hsl(var(--ff-primary))]" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg">{obj.name}</h3>
                          {obj.description && (
                            <p className="text-sm text-muted-foreground mt-1">{obj.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={statusConfig.variant} className={`gap-1 ${statusConfig.className || ''}`}>
                          <StatusIcon className="h-3 w-3" />
                          {statusConfig.label}
                        </Badge>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`objective-menu-${obj.id}`}>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => { setShowProgressDialog(obj); setProgressValue(obj.current_value.toString()); }}>
                              <TrendingUp className="h-4 w-4 mr-2" />
                              {t("update_progress", "company") || "Update Progress"}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openEditDialog(obj)}>
                              <Edit className="h-4 w-4 mr-2" />
                              {t("edit", "common") || "Edit"}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {obj.status === "active" && (
                              <>
                                <DropdownMenuItem onClick={() => handleStatusChange(obj, "completed")}>
                                  <CheckCircle2 className="h-4 w-4 mr-2" />
                                  {t("mark_completed", "company") || "Mark Completed"}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleStatusChange(obj, "paused")}>
                                  <Pause className="h-4 w-4 mr-2" />
                                  {t("pause", "company") || "Pause"}
                                </DropdownMenuItem>
                              </>
                            )}
                            {obj.status === "paused" && (
                              <DropdownMenuItem onClick={() => handleStatusChange(obj, "active")}>
                                <Flag className="h-4 w-4 mr-2" />
                                {t("resume", "company") || "Resume"}
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              className="text-destructive" 
                              onClick={() => setDeleteConfirm(obj)}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              {t("delete", "common") || "Delete"}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">{t("progress", "goals") || "Progress"}</span>
                        <span className="font-medium">{obj.progress_percentage.toFixed(1)}%</span>
                      </div>
                      <Progress value={obj.progress_percentage} className="h-3" />
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-4">
                          <span>
                            <span className="font-medium">{formatValue(obj.current_value, obj.target_unit, obj.currency)}</span>
                            <span className="text-muted-foreground"> / {formatValue(obj.target_value, obj.target_unit, obj.currency)}</span>
                          </span>
                        </div>
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Calendar className="h-4 w-4" />
                          <span>{obj.days_remaining} {t("days_left", "goals") || "days left"}</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg" data-testid="objective-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingObjective ? (t("edit_objective", "company") || "Edit Objective") : (t("new_objective", "company") || "New Objective")}
            </DialogTitle>
            <DialogDescription>
              {editingObjective ? (t("edit_objective_desc", "company") || "Update objective details") : (t("new_objective_desc", "company") || "Set a new financial objective")}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <Label>{t("objective_type", "company") || "Objective Type"}</Label>
              <Select value={formData.objective_type} onValueChange={handleObjectiveTypeChange}>
                <SelectTrigger data-testid="objective-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="revenue_target">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4" />
                      {t("revenue_target", "company") || "Revenue Target"}
                    </div>
                  </SelectItem>
                  <SelectItem value="client_acquisition">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      {t("client_acquisition", "company") || "Client Acquisition"}
                    </div>
                  </SelectItem>
                  <SelectItem value="sales_volume">
                    <div className="flex items-center gap-2">
                      <ShoppingCart className="h-4 w-4" />
                      {t("sales_volume", "company") || "Sales Volume"}
                    </div>
                  </SelectItem>
                  <SelectItem value="profit_margin">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-4 w-4" />
                      {t("profit_margin", "company") || "Profit Margin"}
                    </div>
                  </SelectItem>
                  <SelectItem value="expense_reduction">
                    <div className="flex items-center gap-2">
                      <TrendingDown className="h-4 w-4" />
                      {t("expense_reduction", "company") || "Expense Reduction"}
                    </div>
                  </SelectItem>
                  <SelectItem value="custom">
                    <div className="flex items-center gap-2">
                      <Target className="h-4 w-4" />
                      {t("custom", "company") || "Custom"}
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>{t("name", "common") || "Name"} *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder={t("objective_name_placeholder", "company") || "e.g., Reach $1M in Revenue"}
                data-testid="objective-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>{t("description", "common") || "Description"}</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder={t("objective_description_placeholder", "company") || "Describe your objective (optional)"}
                rows={2}
                data-testid="objective-description-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("target_value", "company") || "Target Value"} *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.target_value}
                  onChange={(e) => setFormData({...formData, target_value: e.target.value})}
                  placeholder="1000000"
                  data-testid="objective-target-input"
                />
              </div>
              {formData.target_unit === "currency" && (
                <div className="space-y-2">
                  <Label>{t("currency", "common") || "Currency"}</Label>
                  <Select value={formData.currency} onValueChange={(v) => setFormData({...formData, currency: v})}>
                    <SelectTrigger data-testid="objective-currency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                      <SelectItem value="GBP">GBP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <Label>{t("period_type", "company") || "Period"}</Label>
              <Select value={formData.period_type} onValueChange={(v) => setFormData({...formData, period_type: v})}>
                <SelectTrigger data-testid="objective-period-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">{t("monthly", "dashboard") || "Monthly"}</SelectItem>
                  <SelectItem value="quarterly">{t("quarterly", "company") || "Quarterly"}</SelectItem>
                  <SelectItem value="yearly">{t("yearly", "dashboard") || "Yearly"}</SelectItem>
                  <SelectItem value="custom">{t("custom", "company") || "Custom"}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("start_date", "company") || "Start Date"}</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                  data-testid="objective-start-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t("end_date", "company") || "End Date"} *</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                  data-testid="objective-end-date-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>{t("priority", "company") || "Priority"}</Label>
              <Select value={formData.priority.toString()} onValueChange={(v) => setFormData({...formData, priority: parseInt(v)})}>
                <SelectTrigger data-testid="objective-priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">{t("normal", "common") || "Normal"}</SelectItem>
                  <SelectItem value="1">{t("high", "company") || "High"}</SelectItem>
                  <SelectItem value="2">{t("critical", "company") || "Critical"}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} disabled={isSaving}>
              {t("cancel", "common") || "Cancel"}
            </Button>
            <Button onClick={handleSave} disabled={isSaving} data-testid="save-objective-button">
              {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingObjective ? (t("update", "common") || "Update") : (t("create", "common") || "Create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Progress Dialog */}
      <Dialog open={!!showProgressDialog} onOpenChange={() => { setShowProgressDialog(null); setProgressValue(""); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("update_progress", "company") || "Update Progress"}</DialogTitle>
            <DialogDescription>
              {showProgressDialog?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t("current_value", "company") || "Current Value"}</Label>
              <Input
                type="number"
                step="0.01"
                value={progressValue}
                onChange={(e) => setProgressValue(e.target.value)}
                placeholder={showProgressDialog?.target_unit === "currency" ? "0.00" : "0"}
                data-testid="progress-value-input"
              />
              <p className="text-xs text-muted-foreground">
                {t("target", "goals") || "Target"}: {showProgressDialog && formatValue(showProgressDialog.target_value, showProgressDialog.target_unit, showProgressDialog.currency)}
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowProgressDialog(null); setProgressValue(""); }}>
              {t("cancel", "common") || "Cancel"}
            </Button>
            <Button onClick={handleUpdateProgress} data-testid="update-progress-button">
              {t("update", "common") || "Update"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("confirm_delete", "common") || "Confirm Delete"}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("delete_objective_warning", "company") || "Are you sure you want to delete"} "{deleteConfirm?.name}"? {t("action_irreversible", "common") || "This action cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel", "common") || "Cancel"}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t("delete", "common") || "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
